<template>
  <div class="plans-page">
    <!-- Pendências -->
    <section class="plans-unpaid" v-if="planosUnpaid.length > 0 && !selectedPlan">
      <h2>Assinaturas pendentes de pagamento</h2>

      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th><b>Plano</b></th>
              <th><b>Valor</b></th>
              <th><b>Período</b></th>
              <th><b>Data</b></th>
              <th><b>Tipo</b></th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(item, index) in planosUnpaid" :key="index">
              <td>{{ item.plan_details.name }}</td>
              <td>{{ numberToReal(item.plan_details.price) }}</td>
              <td>{{ periodicityLabel(item.plan_details.periodicity) }}</td>
              <td>{{ item.created_at | filterDataHora }}</td>
              <td>{{ paymentTypeLabel(item.pagarme_subscription.payment_method) }}</td>
              <td>
                <a target="_blank" rel="noopener" :href="item.pagarme_subscription.manage_url">Gerenciar</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Seleção / Pagamento -->
    <section class="plans">
      <div v-if="!pagamentoSucesso">
        <!-- STEP 1 -->
        <div v-if="!selectedPlan">
          <header class="plans__header">
            <h1 class="title" v-if="!trial.expired">Seja bem-vindo à vDash!</h1>
            <h1 class="title" v-else>
              <span>Ops.</span>
              Seu período expirou.
            </h1>

            <h2 class="subtitle" v-if="!trial.expired">
              Para prosseguirmos, escolha o plano ideal para o seu negócio.
            </h2>
            <h2 class="subtitle" v-else>
              Para continuar utilizando o vDash, escolha o plano ideal para o seu negócio.
            </h2>

            <p class="text">
              <small>
                Precisa de uma solução customizada? 
                <a href="https://meest.tomticket.com/chat/index?id=EP71083&ac=4549312P25022026105532" target="_blank">clique aqui.</a>.
              </small>
            </p>
          </header>

          <!-- Tabs de período -->
          <div class="period-tabs" v-if="periodOptions.length">
            <button
              v-for="p in periodOptions"
              :key="p"
              class="period-tabs__btn"
              :class="{ '--active': period === p }"
              @click="setPeriod(p)"
              type="button"
            >
              <span class="period-tabs__title">{{ periodicityLabel(p) }}</span>
              <span class="period-tabs__hint" v-if="p !== 30">
                economize {{ savingsTextByPeriod(p) }}
              </span>
            </button>
          </div>

          <!-- Lista de cards -->
          <div class="plan-grid" v-if="displayPlans.length">
            <label
              v-for="plano in displayPlans"
              :key="plano.id"
              class="plan-card"
              :class="{
                '--selected': idPlan === plano.id,
                '--highlight': isBestValueByGroup(plano),
              }"
              :for="`plan-${plano.id}`"
            >
              <input
                class="plan-card__radio"
                type="radio"
                name="selected-plan"
                :id="`plan-${plano.id}`"
                :value="plano.id"
                v-model="idPlan"
              />

              <div class="plan-card__top">
                <div class="plan-card__badges">
                  <span class="badge" v-if="plano.bonus === 1">Especial</span>
                  <span class="badge --primary" v-if="isBestValueByGroup(plano)">Melhor custo-benefício</span>
                </div>

                <div class="plan-card__titles">
                  <div class="plan-card__name">{{ plano.name }}</div>
                  <div class="plan-card__desc">{{ plano.small_description }}</div>
                </div>

                <div class="plan-card__price">
                  <div class="plan-card__price-month">
                    <!-- <span class="muted">A partir de</span> -->
                    <span class="price">
                      <span class="price__currency">R$</span>
                      <span class="price__int">{{ brlParts(plano.price_monthly).int }}</span
                      ><span class="price__sep">,</span
                      ><span class="price__cents">{{ brlParts(plano.price_monthly).cents }}</span>
                      <span class="price__per">/mês</span>
                    </span>
                  </div>

                  <div class="plan-card__price-billing">
                    {{ numberToReal(plano.price) }} cobrados a cada {{ plano.periodicity }} dias
                  </div>
                </div>
              </div>

              <div class="plan-card__body">
                <ul class="plan-card__features">
                  <li>Histórico de até {{ plano.history_days }} dias.</li>
                  <li v-if="plano.can_export_csv">Exportação de relatórios</li>
                  <li>Até {{ plano.max_users }} {{ (plano.max_users > 1) ? 'usuários' : 'usuário' }}</li>
                  <li>Acesso ao dashboard</li>
                  <li>Relatórios</li>
                </ul>

                <details class="plan-card__rules" @toggle="onToggleDetails(plano.id, $event)">
                  <summary>Ver regras do plano</summary>
                  <p class="plan-card__rules-text">{{ plano.plan_rules }}</p>
                </details>
              </div>

              <div class="plan-card__selected-indicator" v-if="idPlan === plano.id">
                Selecionado
              </div>

            </label>
          </div>

          <!-- Planos especiais -->
          <div class="special-plans" v-if="bonusPlansForPeriod.length">
            <h3>Planos especiais disponíveis</h3>

            <div class="plan-grid">
              <label
                v-for="plano in bonusPlansForPeriod"
                :key="plano.id"
                class="plan-card"
                :class="{ '--selected': idPlan === plano.id }"
                :for="`plan-${plano.id}`"
              >
                <input
                  class="plan-card__radio"
                  type="radio"
                  name="selected-plan"
                  :id="`plan-${plano.id}`"
                  :value="plano.id"
                  v-model="idPlan"
                />

                <div class="plan-card__top">
                  <div class="plan-card__badges">
                    <span class="badge --special">Especial</span>
                  </div>

                  <div class="plan-card__titles">
                    <div class="plan-card__name">{{ plano.name }}</div>
                    <div class="plan-card__desc">{{ plano.small_description }}</div>
                  </div>

                  <div class="plan-card__price">
                    <div class="plan-card__price-month">
                      <span class="muted">A partir de</span>
                      <span class="price">
                        <span class="price__currency">R$</span>
                        <span class="price__int">{{ brlParts(plano.price_monthly).int }}</span
                        ><span class="price__sep">,</span
                        ><span class="price__cents">{{ brlParts(plano.price_monthly).cents }}</span>
                        <span class="price__per">/mês</span>
                      </span>
                    </div>

                    <div class="plan-card__price-billing">
                      {{ numberToReal(plano.price) }} cobrados a cada {{ plano.periodicity }} dias
                    </div>
                  </div>
                </div>

                <div class="plan-card__body">
                  <ul class="plan-card__features">
                    <li>Histórico de até {{ plano.history_days }} dias.</li>
                    <li v-if="plano.can_export_csv">Exportação de relatórios</li>
                    <li>Até {{ plano.max_users }} {{ (plano.max_users > 1) ? 'usuários' : 'usuário' }}</li>
                    <li>Acesso ao dashboard</li>
                    <li>Relatórios</li>
                  </ul>

                  <details class="plan-card__rules" @toggle="onToggleDetails(plano.id, $event)">
                    <summary>Ver regras do plano</summary>
                    <p class="plan-card__rules-text">{{ plano.plan_rules }}</p>
                  </details>
                </div>

                <div class="plan-card__selected-indicator" v-if="idPlan === plano.id">
                  Selecionado
                </div>
              </label>
            </div>
          </div>

          <!-- CTA -->
          <div class="button-box">
            <p class="hint" v-if="!idPlan">Selecione um plano para continuar.</p>
            <button
              class="btn step"
              :disabled="!idPlan"
              @click="handleStep"
              type="button"
            >
              Prosseguir para pagamento
            </button>
          </div>
        </div>

        <!-- STEP 2 -->
        <div v-else>
          <div class="button-box --top">
            <button @click="handleStep" class="btn link" type="button">
              <IconArrowLeft /> mudar plano
            </button>
          </div>
          <PaymentsForm :idPlan="idPlan" />
        </div>
      </div>

      <!-- Sucesso -->
      <div class="plans__sucesso" v-else>
        <div class="text-center">
          <img class="plans__image" src="~@/assets/images/icones/icon-check-lg.svg" />
          <h1 class="title">Parabéns!</h1>
          <h2 class="subtitle">
            Assinatura realizada com sucesso! <br />
          </h2>
          <h2 class="subtitle">
            Atualize a página para verificar sua assinatura...
          </h2>

          <div class="button-box --sucesso">
            <button @click="closeSucesss" class="btn step" type="button">
              Atualizar
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from "vuex";
import PaymentsForm from "@/components/payments-form/PaymentsForm.vue";
import IconArrowLeft from "@/components/icones/IconArrowLeft.vue";
import { GetPlansAvailable, GetPlans, GetPlansUnpaid, VerifyTrial } from "@/api/plano";
import { numberToReal } from "@/utils/money";
import { filterDataHora } from "@/utils/date";

export default {
  props: ["showModal"],
  components: { PaymentsForm, IconArrowLeft },
  data() {
    return {
      period: 365,
      idPlan: null,
      selectedPlan: false,
      openDetailsId: null,
    };
  },
  filters: { filterDataHora },
  computed: {
    ...mapState({
      planosDisponiveis: (state) => state.plano.planosDisponiveis,
      planosUnpaid: (state) => state.plano.planosUnpaid,
      planosBonus: (state) => state.plano.planosBonus,
      trial: (state) => state.plano.verifyTrial,
      pagamentoSucesso: (state) => state.pagamento.pagamentoSucesso,
    }),

    // Apenas planos ativos (status=1)
    activePlans() {
      const list = Array.isArray(this.planosDisponiveis) ? this.planosDisponiveis : [];
      return list.filter((p) => Number(p.status) === 1);
    },

    // Períodos disponíveis dinamicamente a partir do JSON
    periodOptions() {
      const uniq = Array.from(new Set(this.activePlans.map((p) => Number(p.periodicity)))).sort((a, b) => a - b);
      // se quiser forçar uma ordem específica:
      const order = [30, 90, 365];
      return uniq.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    },

    // Planos normais no período atual
    displayPlans() {
      return this.activePlans
        .filter((p) => Number(p.bonus) === 0)
        .filter((p) => Number(p.periodicity) === Number(this.period))
        .sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly));
    },

    // Planos especiais no período atual
    bonusPlansForPeriod() {
      return this.activePlans
        .filter((p) => Number(p.bonus) === 1)
        .filter((p) => Number(p.periodicity) === Number(this.period))
        .sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly));
    },

    monthlyBaseline() {
      // usa o plano mensal (periodicity 30) mais barato como base de economia
      const monthly = this.activePlans
        .filter((p) => Number(p.bonus) === 0)
        .filter((p) => Number(p.periodicity) === 30)
        .sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly))[0];

      return monthly ? Number(monthly.price_monthly) : null;
    },
  },

  mounted() {
    this.GetPlansAvailable();
    this.GetPlans();
    this.GetPlansUnpaid();
  },

  watch: {
    // garante período válido assim que carregar
    periodOptions: {
      immediate: true,
      handler(opts) {
        if (!opts || !opts.length) return;
        if (!opts.includes(this.period)) this.period = opts[0];
      },
    },

    // sempre que mudar o período, reseta seleção e atualiza store (se você usa isso em outro lugar)
    period() {
      this.idPlan = null;
      this.$store.commit("plano/SET_PLANOS_SELECIONADOS", this.displayPlans);
      // opcional: também recalcula bonus no store
      this.$store.commit("plano/SET_PLANOS_BONUS", this.activePlans.filter((p) => Number(p.bonus) === 1));
    },
  },

  methods: {
    GetPlansAvailable,
    GetPlans,
    GetPlansUnpaid,
    VerifyTrial,
    numberToReal,

    handleStep() {
      if (!this.selectedPlan && !this.idPlan) return;
      this.selectedPlan = !this.selectedPlan;
    },

    closeSucesss() {
      this.$store.commit("pagamento/SET_PAYMENT_SUCCESSS", false);
      this.$store.commit("plano/SET_MODAL", false);
      this.VerifyTrial();
    },

    setPeriod(p) {
      this.period = Number(p);
    },

    periodicityLabel(value) {
      const v = Number(value);
      if (v === 30) return "Mensal";
      if (v === 90) return "Trimestral";
      if (v === 365) return "Anual";
      return `${v} dias`;
    },

    paymentTypeLabel(value) {
      if (value === "credit_card") return "Cartão de crédito";
      if (value === "boleto") return "Boleto";
      return value || "-";
    },

    // "Melhor custo-benefício": destaca o menor price_monthly do período atual (não-bonus)
    isBestValueByPrice(plano) {
      const list = this.displayPlans;
      if (!list.length) return false;
      const best = list.reduce((min, p) => (Number(p.price_monthly) < Number(min.price_monthly) ? p : min), list[0]);
      return best && best.id === plano.id;
    },

    isBestValueByPeriodicity(plano) {
      return Number(plano.periodicity) === 365;
    },

    isBestValueByGroup(plano) {
      return plano.plan_group === 'growth';
    },

    brlParts(value) {
      // aceita number/string
      const n = Number(value || 0);
      const fixed = n.toFixed(2); // "199.00"
      const [intPart, cents] = fixed.split(".");
      // separador de milhar simples (pt-BR)
      const int = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return { int, cents };
    },

    formatInt(n) {
      const v = Number(n || 0);
      return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    savingsTextByPeriod(period) {
      // economia percentual vs mensal (baseline)
      if (!this.monthlyBaseline) return "";
      const list = this.activePlans
        .filter((p) => Number(p.bonus) === 0)
        .filter((p) => Number(p.periodicity) === Number(period))
        .sort((a, b) => Number(a.price_monthly) - Number(b.price_monthly));

      const best = list[0];
      if (!best) return "";

      const saving = 1 - Number(best.price_monthly) / Number(this.monthlyBaseline);
      const pct = Math.round(Math.max(0, saving) * 100);
      return pct > 0 ? `${pct}%` : "0%";
    },

    onToggleDetails(id, evt) {
      if (evt && evt.target && evt.target.open) this.openDetailsId = id;
    },
  },
};
</script>

<style lang="scss" scoped>
.plans-page {
  width: 100%;
}

.plans-unpaid {
  margin-bottom: 28px;

  h2 {
    color: $dark;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .table-wrap {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #fff;
  }
}

.plans {
  display: flex;
  justify-content: center;
  width: 100%;

  &__header {
    max-width: 900px;
    margin: 0 auto 18px auto;
    text-align: center;

    .text small {
      display: inline-block;
      margin-top: 6px;
    }
  }

  &__sucesso {
    min-width: 30vw;
    min-height: 520px;
    align-items: center;
    padding-top: 8rem;
  }

  &__image {
    margin-bottom: 20px;
  }
}

/* Tabs */
.period-tabs {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin: 18px 0 14px 0;
  flex-wrap: wrap;

  &__btn {
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    border-radius: 999px;
    padding: 10px 14px;
    cursor: pointer;
    transition: 160ms ease;
    text-align: left;
    min-width: 160px;

    &:hover {
      transform: translateY(-1px);
      border-color: rgba(0, 0, 0, 0.2);
    }

    &.--active {
      border-color: #ff4876;
      box-shadow: 0 6px 18px rgba(255, 72, 118, 0.18);
    }
  }

  &__title {
    display: block;
    font-weight: 700;
    color: $dark;
    line-height: 1.1;
  }

  &__hint {
    display: block;
    font-size: 12px;
    margin-top: 2px;
    color: $gray;
  }
}

/* Grid */
.plan-grid {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

/* Card */
.plan-card {
  position: relative;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.10);
  overflow: hidden;
  cursor: pointer;
  transition: 180ms ease;
  display: flex;
  flex-direction: column;
  min-height: 260px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 26px rgba(0, 0, 0, 0.10);
    border-color: rgba(0, 0, 0, 0.16);
  }

  &.--selected {
    border-color: #ff4876;
    box-shadow: 0 12px 30px rgba(255, 72, 118, 0.16);

    &.--highlight {
      border-color: rgb(98, 133, 21);
    }
  }

  &.--highlight {
    // border-color: rgba(255, 72, 118, 0.45);
    // background: linear-gradient(180deg, rgba(255, 72, 118, 0.06), rgba(255, 72, 118, 0.00));
    border-color: rgba(179, 255, 72, 0.45);
    background: linear-gradient(180deg, rgba(197, 255, 72, 0.453), rgba(176, 255, 72, 0));
  }

  &__radio {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__top {
    padding: 14px 14px 10px 14px;
  }

  &__badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  &__titles {
    margin-bottom: 10px;
  }

  &__period {
    font-weight: 700;
    color: $dark;
    font-size: 13px;
    letter-spacing: 0.2px;
  }

  &__name {
    font-weight: 600;
    color: $dark;
    margin-top: 2px;
    font-size: 16px;
  }

  &__desc {
    color: $gray;
    font-size: 13px;
    margin-top: 4px;
    min-height: 34px;
  }

  &__price {
    margin-top: 10px;
  }

  &__price-month {
    display: flex;
    gap: 8px;
    align-items: baseline;

    .muted {
      font-size: 12px;
      color: $gray;
    }
  }

  &__price-billing {
    margin-top: 6px;
    font-size: 12px;
    color: $gray;
  }

  &__body {
    padding: 0 14px 14px 14px;
    margin-top: auto;
  }

  &__features {
    margin: 8px 0 10px 0;
    padding-left: 16px;
    color: $gray;
    font-size: 13px;
    list-style: circle;

    li {
      margin: 6px 0;
      line-height: 1.25;
    }

    b {
      color: $dark;
    }
  }

  &__rules {
    summary {
      cursor: pointer;
      font-weight: 700;
      color: $dark;
      font-size: 13px;
      outline: none;
    }

    &-text {
      margin-top: 8px;
      color: $gray;
      font-size: 12px;
      line-height: 1.35;
    }
  }

  &__selected-indicator {
    position: absolute;
    right: 12px;
    bottom: 12px;
    background: #4891ff;
    color: #fff;
    font-weight: 700;
    font-size: 11px;
    padding: 6px 10px;
    border-radius: 999px;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 14px;
  // font-weight: 700;
  background: rgba(0, 0, 0, 0.06);
  color: $dark;

  &.--primary {
    background: rgb(255, 255, 255);
    color: #2f580c;
  }
  &.--special {
    background: rgba(255, 72, 118, 0.14);
    color: #ff4876;
  }
}

.price {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;

  &__currency {
    font-weight: 600;
    color: $dark;
    font-size: 14px;
  }

  &__int {
    font-weight: 600;
    color: $dark;
    font-size: 26px;
    letter-spacing: -0.5px;
  }

  &__sep {
    font-weight: 600;
    color: $dark;
    font-size: 18px;
    margin: 0 1px;
  }

  &__cents {
    font-weight: 600;
    color: $dark;
    font-size: 14px;
  }

  &__per {
    color: $gray;
    font-size: 12px;
    margin-left: 4px;
  }
}

.special-plans {
  max-width: 980px;
  margin: 28px auto 0 auto;

  h3 {
    font-weight: 600;
    color: $dark;
    margin: 8px 0 12px 0;
    text-align: center;
  }
}

.button-box {
  margin-top: 18px;
  text-align: center;

  &.--top {
    margin-bottom: 10px;
    text-align: left;
  }

  .btn.step {
    // background-color: #588518;
    background-color: #579914;
    color: white;
    // border-radius: 40px;
    border-radius: 8px;
    padding: 10px 20px;
  }

  .hint {
    margin-bottom: 8px;
    margin-top: 8px;
    font-size: 12px;
    color: $gray;
  }
}

/* Mantém seu hover do link */
a {
  color: $gray;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #ff4876;
  }
}
</style>
